import React, { useCallback, useEffect, useMemo } from 'react'
import { Grid, makeStyles } from '@material-ui/core'
import { FormProvider, useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import { saveAccountTags } from '../../../../service'
import { TAX_STATUS_OPTIONS, TAX_STATUS_VALUES } from '../../../../constants'
import { useBoolean, useCheckPolicy } from '../../../../hooks'
import { getGroupedTagsBody } from '../LevelTags/helpers'
import { ACCOUNTS } from '../../../../policies/admin'
import Text from '../../../atoms/Text'
import { ACCOUNT_FORM_NAMES, formatDate, saveAccountStatus } from './helpers'
import AccountDetailsTags from './AccountDetailsTags'
import FormSelectField from './FormSelectField'
import TitleWithEditButton from './TitleWithEditButton'
import SubmitButtons from './SubmitButtons'
import AccountCloseButton from './AccountCloseButton'
import PerformanceDateInput from './PerformanceDateInput'

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%'
  }
}))

export const mapFormDefaultValues = (account) => {
  if (!account) return null

  const extraFields = {}

  const { tags = [] } = account

  return {
    ...extraFields,
    [ACCOUNT_FORM_NAMES.taxStatusId]: account?.taxStatusId,
    [ACCOUNT_FORM_NAMES.tags]: tags || []
  }
}

const AccountGeneralInfo = ({
  account,
  loading,
  onSave,
  onClosedAccount,
  disableEdit,
  setDisableEdit
}) => {
  const [editMode, setEditMode] = useBoolean()
  const [formLoading, setFormLoading] = useBoolean()
  const classes = useStyles({ editMode })
  const canCloseAccounts = useCheckPolicy(ACCOUNTS.closeAccounts)
  const userCanEditTags = useCheckPolicy(ACCOUNTS.editAccountTags)

  const defaultValues = useMemo(() => {
    return mapFormDefaultValues(account)
  }, [account])

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {}
  })

  const {
    reset,
    handleSubmit,
    watch,
    register,
    formState: { isValid }
  } = formMethods

  useEffect(() => {
    reset(defaultValues)
  }, [reset, defaultValues])

  useEffect(() => {
    return () => setDisableEdit.off()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const closeDateRender = useMemo(() => {
    if (canCloseAccounts && !account.closeDate) {
      return (
        <AccountCloseButton
          editMode
          account={account}
          onClosedAccount={onClosedAccount}
        />
      )
    }
    return formatDate(account.closeDate)
  }, [account, canCloseAccounts, onClosedAccount])

  const accountDates = useMemo(() => {
    const { asOfDate, startDate, dailyStartDate } = account
    return [
      { key: 'startDate', label: 'Start Date', text: formatDate(startDate) },
      { key: 'dailyStartDate', label: 'Daily Start Date', text: formatDate(dailyStartDate || startDate) },
      { key: 'asOfDate', label: 'As Of Date', text: formatDate(asOfDate) },
      {
        key: 'performanceStartDate',
        label: '',
        text: (
          <PerformanceDateInput
            account={account}
            onSave={(val) => { account.performanceStartDate = val }}
            disableEdit={editMode}
          />
        )
      },
      {
        key: 'closeDate',
        label: 'Close Date',
        text: closeDateRender
      }
    ]
  }, [account, closeDateRender, editMode])

  const onSubmit = useCallback(
    async (values) => {
      try {
        setFormLoading.on()
        const accountTags = getGroupedTagsBody(values.tags, account.tags)
        await Promise.all([
          saveAccountStatus({
            account,
            newValues: {
              taxStatusId: values.taxStatusId
            }
          }),
          (userCanEditTags && saveAccountTags(account.accountId, accountTags))
        ])
        onSave()
      } catch (err) {
        console.error(err)
      } finally {
        setDisableEdit.off()
        setEditMode.off()
        setFormLoading.off()
      }
    },
    [setFormLoading, account, userCanEditTags, onSave, setDisableEdit, setEditMode]
  )

  const onResetForm = useCallback(() => {
    reset(mapFormDefaultValues(account))
  }, [account, reset])

  const onCancel = useCallback(() => {
    onResetForm()
    setDisableEdit.off()
    setEditMode.off()
  }, [onResetForm, setEditMode, setDisableEdit])

  const handleEditMode = useCallback(() => {
    setEditMode.toggle()
    setDisableEdit.toggle()
  }, [setEditMode, setDisableEdit])

  return (
    <div className={classes.container}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container>
            <TitleWithEditButton
              text='Account Information'
              onClick={handleEditMode}
              editMode={editMode}
              disabled={!editMode && disableEdit}
              hideEditButton={!userCanEditTags}
            />
            <Grid container style={{ marginBottom: '2rem' }}>
              {accountDates.map(({ key, label, text }) => (
                <Grid item xs={6} sm={3} key={key} style={{ marginBottom: '.5rem' }}>
                  <Text customFontSize='18px' text={label} />
                  <Text customFontSize='18px' text={text} />
                </Grid>
              ))}
            </Grid>
            <Grid item xs={12}>
              <FormSelectField
                label='Tax Status'
                accessor={ACCOUNT_FORM_NAMES.taxStatusId}
                editMode={editMode && userCanEditTags}
                value={watch(ACCOUNT_FORM_NAMES.taxStatusId)}
                options={TAX_STATUS_OPTIONS}
                text={TAX_STATUS_VALUES[watch(ACCOUNT_FORM_NAMES.taxStatusId)]}
                InputProps={{
                  ...register(ACCOUNT_FORM_NAMES.taxStatusId),
                  disableUnderline: true
                }}
              />
            </Grid>
            <Grid container item xs={12}>
              <AccountDetailsTags editMode={editMode} loading={loading} />
            </Grid>
          </Grid>
          <SubmitButtons
            editMode={editMode}
            onCancel={onCancel}
            isSubmitting={formLoading}
            isFormValid={isValid}
          />
        </form>
      </FormProvider>
    </div>
  )
}

AccountGeneralInfo.propTypes = {
  account: PropTypes.object,
  onSave: PropTypes.func,
  onClosedAccount: PropTypes.func,
  loading: PropTypes.bool,
  disableEdit: PropTypes.bool,
  setDisableEdit: PropTypes.object
}

export default AccountGeneralInfo
